import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* ============================
   THUNKS

   These used to point at Node's /api/customers, which this service does not
   serve — there was no admin customer surface at all, and the header of this
   file said so. There is one now: GET/PUT /admin/customers and
   PATCH /admin/customers/{id}/status, in Api/AdminCustomerEndpoints.cs.

   ── THERE IS STILL NO ADMIN DELETE, AND THAT IS DELIBERATE ──
   Erasure is POST /customer/delete_my_data, carried out by the person with
   their own token. It is irreversible, it drops addresses by cascade, and
   under the DPDP Act it is the data principal's right to exercise. An
   admin-facing delete turns a statutory right into a staff convenience and the
   first mis-click cannot be undone. Suspension covers the operational need —
   stopping someone ordering — and can be reversed.

   ── WHY THE ROWS STILL CARRY `_id` ──
   The service keys customers on a bigint `id`. The list endpoint casts it to
   text and names it `_id` so these reducers and the screen keep matching rows
   the way they always have. That is a deliberate shim at one boundary rather
   than a rename spread across a slice, a screen and three endpoints.
============================ */

export const fetchCustomers = createAsyncThunk(
  "customer/fetchAll",
  async ({ page = 1, limit = 10, search = "" }, thunkAPI) => {
    try {
      const res = await api.get("/admin/customers", {
        params: { page, limit, search },
      });
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message ||
          err.response?.data?.code ||
          "Could not load the customer list."
      );
    }
  }
);

export const updateCustomer = createAsyncThunk(
  "customer/update",
  async ({ id, data }, thunkAPI) => {
    try {
      const res = await api.put(`/admin/customers/${id}`, data);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message ||
          err.response?.data?.code ||
          "That did not save."
      );
    }
  }
);

export const toggleCustomerStatus = createAsyncThunk(
  "customer/toggleStatus",
  async (id, thunkAPI) => {
    try {
      const res = await api.patch(`/admin/customers/${id}/status`);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data?.message ||
          err.response?.data?.code ||
          "Could not change that."
      );
    }
  }
);

/* ============================
   SLICE
============================ */
const customerSlice = createSlice({
  name: "customer",
  initialState: {
    customers: [],
    loading: false,
    error: null,
    pagination: {},
  },
  reducers: {
    clearCustomerError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      /* ── Fetch ── */
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.customers = action.payload.data || [];
        state.pagination = action.payload.pagination || {};
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ── Update ── */
      .addCase(updateCustomer.fulfilled, (state, action) => {
        const at = state.customers.findIndex(
          (c) => c._id === action.payload._id
        );
        if (at !== -1) state.customers[at] = action.payload;
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.error = action.payload;
      })

      /* ── Suspend / reinstate ── */
      .addCase(toggleCustomerStatus.fulfilled, (state, action) => {
        const at = state.customers.findIndex(
          (c) => c._id === action.payload._id
        );
        if (at !== -1) state.customers[at] = action.payload;
      })
      .addCase(toggleCustomerStatus.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearCustomerError } = customerSlice.actions;
export default customerSlice.reducer;
